import React, { memo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useObservable, useCallbackObservable } from 'react-use-observable';

import { CopyOutlined as FileCopyIcon, ReloadOutlined as RefreshIcon } from '@ant-design/icons';
import { Button, Col, Row, Spin } from 'antd';

import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import HelperPage from 'v2/components/Shared/HelperPage';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateFormat } from 'v2/formatters/date';
import configAPIService from 'v2/services/configAPI';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import APIInativationAlert from './APIInactivationAlert';
import { NewApiKeyDialog } from './newApiKeyDialog';

export const ConfigAPI = memo(() => {
  useObservable(() => configAPIService.loadApiKey(), []);
  useObservable(() => configAPIService.loadOriginKey(), []);

  const [showApiKey, setShowApiKey] = useState<boolean>(false);

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const [loadingApiKey] = useObservable(() => configAPIService.isLoadingApiKey(), []);
  const [apiKey] = useObservable(() => configAPIService.getApiKey(), []);

  const [loadingOriginKey] = useObservable(() => configAPIService.isLoadingOriginKey(), []);
  const [originKey] = useObservable(() => configAPIService.getOriginKey(), []);

  const [open, setOpen] = useState<boolean>(false);

  const [updateOriginKey] = useCallbackObservable(
    () =>
      configAPIService.updateOriginKey().pipe(
        tap(() => {
          toast.success('API key atualizada com sucesso');
        }),
        catchError(() => {
          toast.error('Ocorreu um erro ao tentar gerar uma nova Origin Key');
          return of(true);
        })
      ),
    []
  );

  const onClose = () => {
    setShowApiKey(true);
    setOpen(false);
  };

  useEffect(() => () => setShowApiKey(false), []);

  const getShowKey = () => {
    if (apiKey?.deprecated_api_key) return apiKey.deprecated_api_key;
    if (showApiKey && apiKey?.api_key) return apiKey?.api_key;
    if (apiKey?.api_key_last_digits) return `*********${apiKey?.api_key_last_digits}`;
    return '*************';
  };

  const currentApiKey = getShowKey();

  const listKeys = [
    {
      name: 'Public Key',
      key: `${user?.id}`,
      canCopy: true,
      generated_at: '',
      loading: false
    },
    {
      name: 'API Key',
      key: currentApiKey,
      canCopy: showApiKey,
      generated_at: apiKey?.api_key_generated_at,
      loading: loadingApiKey
    },
    {
      name: 'Origin Key',
      key: originKey?.origin_secret ?? 'Não gerada',
      canCopy: !!originKey?.origin_secret,
      generated_at: originKey?.origin_secret_generated_at,
      loading: loadingOriginKey
    }
  ];

  return (
    <>
      <APIInativationAlert />
      <br />
      <div className='m-auto flex w-full flex-col items-center justify-center'>
        <div className='relative flex w-full max-w-[600px] flex-row items-center justify-center max-sm:flex-col-reverse'>
          <div className='text-text-heading [&_p]:text-grey-400 mb-6 w-full items-center justify-between text-center [&_h1]:mb-2 [&_h1]:text-[2rem] [&_h1]:leading-none [&_h1]:font-normal [&_p]:text-base [&_p]:leading-5.25'>
            <h1>API Eduzz</h1>
            <p>Gerencie suas chaves de integração</p>
          </div>
          <div className='absolute top-0 right-0 max-sm:relative max-sm:mb-4 max-sm:self-end'>
            <HelperPage id='config-api-button' title='Configuração de API' name='config_api' />
          </div>
        </div>
        <div className='w-full max-w-[600px] p-6'>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div>
                {listKeys.map(keyItem => (
                  <div key={keyItem.name} className='flex items-center justify-between border-b border-gray-100 py-3'>
                    <div>
                      <div className='text-text-heading mb-[6px] font-semibold'>{keyItem.name}</div>
                      {keyItem.loading ? (
                        <Spin size='small' />
                      ) : (
                        <span className='text-xs'>
                          <Truncate count={40} final='...' title={keyItem.key}>
                            {keyItem.key}
                          </Truncate>
                        </span>
                      )}
                      {!keyItem.loading && keyItem.generated_at && (
                        <div className='text-grey-600 text-xs'>
                          Criado em: {dateFormat(keyItem.generated_at, 'dd/MM/yyyy HH:mm')}
                        </div>
                      )}
                    </div>
                    {keyItem.canCopy && (
                      <Button
                        type='text'
                        icon={<FileCopyIcon />}
                        onClick={() => navigator.clipboard.writeText(keyItem.key)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Col>

            <Col span={24}>
              <p className='mb-1 pt-2 text-sm font-light'>* Não compartilhe essas informações com ninguém!</p>
            </Col>

            <Col xs={24} md={12}>
              <Button
                icon={loadingOriginKey ? <Spin size='small' /> : <RefreshIcon />}
                block
                type='primary'
                disabled={loadingOriginKey}
                onClick={updateOriginKey}
              >
                Gerar nova Origin key
              </Button>
            </Col>
            <Col xs={24} md={12}>
              <Button
                icon={loadingApiKey ? <Spin size='small' /> : <RefreshIcon />}
                block
                type='primary'
                disabled={loadingApiKey}
                onClick={() => setOpen(true)}
              >
                Gerar nova API key
              </Button>
            </Col>
          </Row>
        </div>
      </div>
      <NewApiKeyDialog open={open} onClose={onClose} onConfirm={onClose} />
    </>
  );
});
